import CustomStore from "devextreme/data/custom_store";
import * as Query from "../../framework/query";
import { fetchJSON } from "../../framework/api";

/**
 * @returns {import("../datagrid").Plugin}
 */
export function serverDataSourcePlugin(url) {
  return {
    id: "serverDataSource",
    options: {
      remoteOperations: {
        paging: true,
        filtering: true,
        sorting: true,
        // See https://js.devexpress.com/jQuery/Documentation/ApiReference/UI_Components/dxDataGrid/Configuration/remoteOperations/
        // For now we don't do grouping or summary but that may be useful, especially for Activity Reports.
        grouping: false,
        summary: false,
      },
      // Default `true` makes filter-row lookup SelectBoxes group the main store to show only values present in the current filter.
      // With remote data that's a full-dataset fetch!!!
      // Disable so SelectBoxes use `column.lookup.dataSource` verbatim. Trade-off: dropdowns may show values with no matching row.
      // No per-column toggle, this has to be table level.
      syncLookupFilterValues: false,
      paging: {
        pageSize: 100,
        enabled: true,
      },
      pager: {
        // Keep the pager (and its `showInfo` total) visible even when results
        // fit on a single page. The default `"auto"` hides it, which would hide
        // the total count as soon as filters narrow the list below one page.
        visible: true,
        showPageSizeSelector: true,
        allowedPageSizes: [100, 200, 500],
        showInfo: true,
        showNavigationButtons: true,
      },
      scrolling: {
        mode: "standard",
      },
      dataSource: createServerSideDataSource(url),
    },
    customizeColumns(columns) {
      for (const column of columns) {
        // By default, filter values of date columns are stored as DateTime due to how
        // dates and handled in JavaScript. Since they are date time, they are localized,
        // and change based on the user timezone. We want to avoid that and actually use
        // the date the over selected as the filter value.
        if (column.dataType === "date") {
          column.calculateFilterExpression = calculateDateFilterExpression;
        }
      }

      return columns;
    },
  };
}

/**
 * Creates a DevExtreme CustomStore for server-side operations (pagination, filtering, sorting)
 *
 * @param {string} baseUrl - The base URL for the API endpoint
 * @returns {CustomStore} DevExtreme CustomStore instance
 *
 * @example
 * const dataSource = createServerSideDataSource("/ajax/companies/tabular");
 */
function createServerSideDataSource(baseUrl) {
  // eslint-disable-next-line no-console
  const log = Query.getParam("debug") === "true" ? console.log : () => {};

  // https://js.devexpress.com/jQuery/Documentation/Guide/Data_Binding/Specify_a_Data_Source/Custom_Data_Sources/
  return new CustomStore({
    key: "id",

    load: async (loadOptions) => {
      log("DevExtreme Load Options:", loadOptions);

      const loadSinglePage = async function (loadOptions) {
        const params = buildQueryParams(loadOptions);
        const url = `${baseUrl}?${params}`;

        log("Request URL:", url);

        const response = await fetchJSON(url);

        log("Server Response:", response);

        return response;
      };

      const loadAllPages = async function (loadOptions) {
        log("Loading all pages...");

        // Use a reasonable page size for bulk loading
        const pageSize = 500;
        let allData = [];
        let skip = 0;
        let totalCount = null;

        try {
          while (true) {
            const pageLoadOptions = {
              ...loadOptions,
              take: pageSize,
              skip: skip,
            };

            log(`Loading page ${Math.floor(skip / pageSize) + 1}...`);

            const result = await loadSinglePage(pageLoadOptions);

            const pageData = result.data;
            totalCount = result.totalCount;

            // If no data returned, we're done
            if (!pageData || pageData.length === 0) {
              break;
            }

            allData = allData.concat(pageData);
            skip += pageSize;

            // If we got less than a full page, or we've loaded everything
            if (pageData.length < pageSize || allData.length >= totalCount) {
              break;
            }
          }

          log(`Loaded all pages. Total records: ${allData.length}`);

          return {
            data: allData,
            totalCount: totalCount,
          };
        } catch (error) {
          console.error("Error loading all pages:", error);
          throw new Error(`Failed to load all pages: ${error.message}`);
        }
      };

      try {
        // Check if we need to load all pages
        if (typeof loadOptions.skip === "undefined" || typeof loadOptions.take === "undefined") {
          return await loadAllPages(loadOptions);
        }

        return await loadSinglePage(loadOptions);
      } catch (error) {
        console.error("Error loading data:", error);
        throw new Error(`Failed to load data: ${error.message}`);
      }
    },
  });
}

/**
 * Build query parameters from DevExtreme load options
 *
 * @param {Object} loadOptions - DevExtreme load options
 * @returns {string} URL query string
 */
function buildQueryParams(loadOptions) {
  const params = new URLSearchParams();

  // Pagination parameters
  if (loadOptions.take !== undefined) {
    params.append("take", loadOptions.take);
  }
  if (loadOptions.skip !== undefined) {
    params.append("skip", loadOptions.skip);
  }

  // Sorting parameters
  if (loadOptions.sort && loadOptions.sort.length > 0) {
    const sortParams = loadOptions.sort.map((sort) => ({
      field: sort.selector,
      direction: sort.desc ? "desc" : "asc",
    }));
    params.append("sort", JSON.stringify(sortParams));
  }

  // Filtering parameters
  if (loadOptions.filter && loadOptions.filter.length > 0) {
    params.append("filter", JSON.stringify(loadOptions.filter));
  }

  // Search parameters
  if (loadOptions.searchValue) {
    params.append("searchValue", loadOptions.searchValue);
    if (loadOptions.searchOperation) {
      params.append("searchOperation", loadOptions.searchOperation);
    }
  }

  return params.toString();
}

function calculateDateFilterExpression(date, selectedFilterOperation, _target) {
  const dateString = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  return [this.dataField, selectedFilterOperation, dateString];
}

function pad(number) {
  return number.toString().padStart(2, "0");
}
